import React, { useEffect, useState, useMemo } from 'react';
import api from '../services/api';
import {
  ResponsiveContainer,
  LineChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Area,
} from 'recharts';

function currency(v){ return '₹' + (Number(v) || 0).toLocaleString(); }

function CustomTooltip({ active, payload, label }){
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div style={{ background: 'rgba(0,0,0,0.8)', padding: 8, borderRadius: 8, boxShadow: '0 6px 18px rgba(2,6,23,0.12)', fontSize: 13 }}>
      <div style={{ fontWeight: 700, marginBottom: 6, color: '#00fff0' }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, color: p.color }}>
          <div style={{ opacity: 0.9, color: 'rgba(230,247,255,0.85)' }}>{p.name || p.dataKey}</div>
          <div style={{ fontWeight: 700, color: '#ffb2e6' }}>{p.dataKey === 'revenue' ? currency(p.value) : (p.value || 0)}</div>
        </div>
      ))}
    </div>
  );
}

export default function Reports(){
  const [data,setData] = useState(null);
  const [byEvent,setByEvent] = useState([]);
  const [eventsMeta, setEventsMeta] = useState([]);
  const [trend,setTrend] = useState([]);
  const [range, setRange] = useState(30);
  const [loading, setLoading] = useState(true);
  const [modalEvent, setModalEvent] = useState(null);

  useEffect(()=>{
    load();
  },[range]);

  async function load(){
    try{
      setLoading(true);
      const r = await api.get('/reports/financial');
      setData(r.data.summary || null);
      setByEvent(r.data.byEvent || []);
      setTrend(r.data.trend || []);
      // fetch events meta (ticket_price, capacity) to compute budgets/profit
      try {
        const ev = await api.get('/events');
        setEventsMeta(Array.isArray(ev.data) ? ev.data : []);
      } catch (err) {
        setEventsMeta([]);
      }
    }catch(e){ /* ignore */ } finally { setLoading(false); }
  }

  // prepare chart data (ensure continuity)
  const chartData = useMemo(()=>{
    if (!trend || trend.length === 0) return [];
    return trend.map(t => ({ name: t.day || t.date, bookings: Number(t.bookingsCount || 0), revenue: Number(t.revenue || 0) }));
  },[trend]);

  // CSV export helper
  function exportCSV(){
    const rows = [];
    rows.push(['Event','Revenue','Seats Sold','Ticket Price','Capacity','Potential Revenue','Profit/Loss']);
    byEvent.forEach(ev => {
      const meta = (eventsMeta || []).find(m => Number(m.id) === Number(ev.id)) || {};
      const ticket_price = Number(meta.ticket_price || meta.ticketPrice || 0) || 0;
      const capacity = Number(meta.capacity || 0) || 0;
      const revenue = Number(ev.revenue || 0) || 0;
      const potential = Math.round(ticket_price * (capacity || Number(ev.seats_sold || 0)));
      const profit = Math.round(revenue - potential);
      rows.push([ev.name, revenue, ev.seats_sold, ticket_price, capacity, potential, profit]);
    });
    rows.push([]);
    rows.push(['Trend Date','Bookings','Revenue']);
    trend.forEach(t => rows.push([t.day || t.date, t.bookingsCount, t.revenue]));
    const csv = rows.map(r => r.map(c => '"' + String(c).replace(/"/g,'""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'reports.csv'; a.click(); URL.revokeObjectURL(url);
  }

  return (
    <div className="p-6 cyber-dashboard" style={{ minHeight: '100vh', fontFamily: 'Poppins, system-ui, Arial' }}>
      <h2 className="text-2xl mb-4 neon-title" style={{ fontWeight: 700 }}>Reports</h2>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="cyber-card" style={{ padding: 16, borderRadius: 12 }}>
          <div style={{ fontSize: 13, color: 'rgba(230,247,255,0.8)' }}>Financial summary</div>
          {loading ? (
            <div>
              <div className="skeleton skeleton-title" />
              <div className="skeleton skeleton-line" style={{ width: '40%' }} />
            </div>
          ) : data ? (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#ffb2e6' }}>{currency(data.totalRevenue)}</div>
              <div style={{ fontSize: 13, color: 'rgba(230,247,255,0.75)' }}>Bookings: {data.bookingsCount}</div>
            </div>
          ) : (
            <div style={{ color: '#6b7280' }}>No data</div>
          )}
        </div>

        <div className="cyber-chart-panel" style={{ padding: 16, borderRadius: 12 }}>
          <div style={{ fontSize: 13, opacity: 0.9, color: 'rgba(230,247,255,0.85)' }}>Bookings trend ({range} days)</div>
          <div style={{ marginTop: 12, height: 240 }}>
            {loading ? (
              <div className="skeleton skeleton-chart" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 18, left: -8, bottom: 6 }}>
                  <defs>
                    <linearGradient id="gRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ff49b4" stopOpacity={0.6}/>
                      <stop offset="95%" stopColor="#ff49b4" stopOpacity={0.05}/>
                    </linearGradient>
                    <linearGradient id="gBookings" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00fff0" stopOpacity={0.6}/>
                      <stop offset="95%" stopColor="#00fff0" stopOpacity={0.05}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="name" tick={{ fill: 'rgba(230,247,255,0.9)', fontSize: 12 }} />
                  <YAxis tick={{ fill: 'rgba(230,247,255,0.9)', fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip/>} />
                  <Legend verticalAlign="top" align="right" wrapperStyle={{ color: 'rgba(230,247,255,0.9)' }} />
                  <Area type="monotone" dataKey="revenue" stroke="#ff49b4" fill="url(#gRevenue)" name="Revenue" />
                  <Line type="monotone" dataKey="bookings" stroke="#00fff0" strokeWidth={3} dot={{ r: 3 }} activeDot={{ r: 6 }} name="Bookings" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="cyber-card" style={{ gridColumn: '1 / span 2', padding: 16, borderRadius: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, color: 'rgba(230,247,255,0.85)', fontWeight: 700 }}>Compare Events — Revenue vs Estimated Budget</div>
            <div><button className="btn btn-sm btn-outline neon-outline-btn" onClick={exportCSV}>Export CSV</button></div>
          </div>

          <div style={{ marginTop: 12, height: 300 }}>
            {loading ? (
              <div className="skeleton skeleton-chart" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byEvent.map(ev => {
                  const meta = (eventsMeta || []).find(m => Number(m.id) === Number(ev.id)) || {};
                  const ticket_price = Number(meta.ticket_price || meta.ticketPrice || 0) || 0;
                  const capacity = Number(meta.capacity || 0) || 0;
                  const potential = Math.round(ticket_price * capacity);
                  const revenue = Number(ev.revenue || 0) || 0;
                  return { name: ev.name, revenue, budget: potential, profit: revenue - potential };
                })} margin={{ top: 10, right: 18, left: 8, bottom: 6 }}>
                  <XAxis dataKey="name" tick={{ fill: 'rgba(230,247,255,0.9)', fontSize: 12 }} />
                  <YAxis tick={{ fill: 'rgba(230,247,255,0.9)', fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip/>} />
                  <Legend verticalAlign="top" />
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <Bar dataKey="budget" stackId="a" fill="#94a3b8" name="Est. Budget" />
                  <Bar dataKey="revenue" stackId="a" fill="#06b6d4" name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="cyber-panel" style={{ gridColumn: '1 / span 2', padding: 16, borderRadius: 12 }}>
           <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
-            <div style={{ fontSize: 13, color: '#6b7280' }}>Revenue by event</div>
-            <div><button className="btn btn-sm btn-outline" onClick={exportCSV}>Export CSV</button></div>
+            <div style={{ fontSize: 13, color: 'rgba(230,247,255,0.85)' }}>Revenue by event</div>
+            <div><button className="btn btn-sm btn-outline neon-outline-btn" onClick={exportCSV}>Export CSV</button></div>
           </div>

           <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 }}>
             {byEvent.map(ev=> (
-              <div key={ev.id} style={{ padding: 12, borderRadius: 10, border: '1px solid #eef2ff' }}>
-                <div style={{ fontSize: 13, color: '#6b7280' }}>{ev.name}</div>
-                <div style={{ fontWeight: 800, fontSize: 18, marginTop: 6 }}>{currency(ev.revenue)}</div>
-                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>Seats sold: {ev.seats_sold}</div>
-              </div>
+              <div key={ev.id} className="cyber-card" style={{ padding: 12, borderRadius: 10 }}>
+                <div style={{ fontSize: 13, color: 'rgba(230,247,255,0.85)' }}>{ev.name}</div>
+                <div style={{ fontWeight: 800, fontSize: 18, marginTop: 6, color: '#ffb2e6' }}>{currency(ev.revenue)}</div>
+                <div style={{ fontSize: 12, color: 'rgba(230,247,255,0.6)', marginTop: 6 }}>Seats sold: {ev.seats_sold}</div>
+              </div>
             ))}
           </div>
         </div>
       </div>
     </div>
   );
 }
